'use client'

import { useLocale } from 'next-intl'
import { Link } from '@/i18n/navigation'
import { brokers , formatCommission } from '@/data/indexData'
import { t } from '@/i18n/translations'
import { trackAffiliateClick } from '@/utils/analytics'

interface BrokerDetail {
  id: string
  name: string
  logo: string
  logoBg?: string  // logo container bg — beyaz/transparent logolar için marka rengi
  descKey: string
  bistCommission: string
  usCommission: string
  euCommission: string
  appStore: string
  playStore: string
  website: string
  highlightKey?: string
}

const allBrokers: BrokerDetail[] = [
  {
    id: 'midas',
    name: 'Midas',
    logo: '/midas-logo.png',
    descKey: 'brokers.midas',
    bistCommission: '₺0',
    usCommission: '$0',
    euCommission: '€0',
    appStore: 'https://apps.apple.com/tr/app/midas/id1564368705',
    playStore: 'https://play.google.com/store/apps/details?id=com.midas.app',
    website: 'https://www.getmidas.com',
    highlightKey: 'brokers.zeroComm',
  },
  {
    id: 'garanti',
    name: 'Garanti BBVA Yatırım',
    logo: '/garanti-logo.png',
    descKey: 'brokers.garanti',
    bistCommission: '%0,150',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/garanti-bbva-yat%C4%B1r%C4%B1m-trader/id1484498498',
    playStore: 'https://play.google.com/store/apps/details?id=com.garantibbva.yatirimtrader',
    website: 'https://www.garantibbvayatirim.com.tr',
  },
  {
    id: 'istefon',
    name: 'İş Yatırım (İşTeFon)',
    logo: '/istefon-logo.png',
    descKey: 'brokers.is',
    bistCommission: '%0,188',
    usCommission: '%0,250',
    euCommission: '%0,250',
    appStore: 'https://apps.apple.com/tr/app/i%C5%9Ftefon/id1456849498',
    playStore: 'https://play.google.com/store/apps/details?id=com.isyatirim.istefon',
    website: 'https://www.isyatirim.com.tr',
  },
  {
    id: 'akbank',
    name: 'Ak Yatırım',
    logo: '/akbank-logo.png',
    descKey: 'brokers.ak',
    bistCommission: '%0,170',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/tradeall-ak-yat%C4%B1r%C4%B1m/id1546389498',
    playStore: 'https://play.google.com/store/apps/details?id=com.akyatirim.tradeall',
    website: 'https://www.akyatirim.com.tr',
  },
  {
    id: 'yapikredi',
    name: 'Yapı Kredi Yatırım',
    logo: '/brokers/yapikredi-yatirim-logo.svg',
    logoBg: '#4A2D82',
    descKey: 'brokers.yapikredi',
    bistCommission: '%0,150',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/yk-yat%C4%B1r%C4%B1m/id1449991812',
    playStore: 'https://play.google.com/store/apps/details?id=com.yapikredi.ykyatirim',
    website: 'https://www.ykyatirim.com.tr',
  },
  {
    id: 'deniz',
    name: 'Deniz Yatırım',
    logo: '/brokers/deniz-yatirim-logo.svg',
    logoBg: '#005073',
    descKey: 'brokers.deniz',
    bistCommission: '%0,150',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/deniz-yat%C4%B1r%C4%B1m-trader/id1481282398',
    playStore: 'https://play.google.com/store/apps/details?id=com.denizyatirim.trader',
    website: 'https://www.denizyatirim.com',
  },
  {
    id: 'teb',
    name: 'TEB Yatırım',
    logo: '/brokers/teb-yatirim-logo.webp',
    descKey: 'brokers.teb',
    bistCommission: '%0,175',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/teb-yat%C4%B1r%C4%B1m/id1469046612',
    playStore: 'https://play.google.com/store/apps/details?id=com.tebyatirim.mobile',
    website: 'https://www.tebyatirim.com.tr',
  },
  {
    id: 'ziraat',
    name: 'Ziraat Yatırım',
    logo: '/brokers/ziraat-yatirim-logo.png',
    descKey: 'brokers.ziraat',
    bistCommission: '%0,180',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/ziraat-yat%C4%B1r%C4%B1m/id1445231312',
    playStore: 'https://play.google.com/store/apps/details?id=com.ziraatbank.ziraatmobil',
    website: 'https://www.ziraatyatirim.com.tr',
  },
  {
    id: 'vakif',
    name: 'Vakıf Yatırım',
    logo: '/brokers/vakif-yatirim-logo.png',
    descKey: 'brokers.vakif',
    bistCommission: '%0,175',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/vak%C4%B1f-yat%C4%B1r%C4%B1m/id1459982512',
    playStore: 'https://play.google.com/store/apps/details?id=com.vakifyatirim.mobile',
    website: 'https://www.vakifyatirim.com.tr',
  },
  {
    id: 'halk',
    name: 'Halk Yatırım',
    logo: '/brokers/halk-yatirim-logo.svg',
    logoBg: '#00853E',
    descKey: 'brokers.halk',
    bistCommission: '%0,180',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/halk-yat%C4%B1r%C4%B1m/id1438592368',
    playStore: 'https://play.google.com/store/apps/details?id=com.halkyatirim.mobile',
    website: 'https://www.halkyatirim.com.tr',
  },
  {
    id: 'qnb',
    name: 'QNB Finans Yatırım',
    logo: '/brokers/qnb-invest-logo.png',
    descKey: 'brokers.qnb',
    bistCommission: '%0,175',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/qnb-invest/id1441349312',
    playStore: 'https://play.google.com/store/apps/details?id=com.qnbfinansyatirim.mobile',
    website: 'https://www.qnbinvest.com.tr',
  },
  {
    id: 'oyak',
    name: 'Oyak Yatırım',
    logo: '/brokers/oyak-yatirim-logo.png',
    descKey: 'brokers.oyak',
    bistCommission: '%0,188',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/oyak-yat%C4%B1r%C4%B1m/id1438592368',
    playStore: 'https://play.google.com/store/apps/details?id=com.oyakyatirim.mobile',
    website: 'https://www.oyakyatirim.com.tr',
  },
  {
    id: 'gedik',
    name: 'Gedik Yatırım',
    logo: '/brokers/gedik-yatirim-logo.png',
    logoBg: '#1A2B6B',
    descKey: 'brokers.gedik',
    bistCommission: '%0,188',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/gedik-trader/id1438592368',
    playStore: 'https://play.google.com/store/apps/details?id=com.gedik.trader',
    website: 'https://gedik.com',
  },
  {
    id: 'tera',
    name: 'Tera Yatırım',
    logo: '/brokers/tera-yatirim-logo.svg',
    descKey: 'brokers.tera',
    bistCommission: '%0,200',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/tera-trader/id1438592368',
    playStore: 'https://play.google.com/store/apps/details?id=com.terayatirim.mobile',
    website: 'https://www.terayatirim.com',
  },
  {
    id: 'gcm',
    name: 'GCM Yatırım',
    logo: '/brokers/gcm-yatirim-logo.png',
    descKey: 'brokers.gcm',
    bistCommission: '%0,200',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/gcm-trader/id1438592368',
    playStore: 'https://play.google.com/store/apps/details?id=com.gcmyatirim.mobile',
    website: 'https://www.gcmyatirim.com.tr',
  },
  {
    id: 'unlu',
    name: 'ÜNLÜ Menkul',
    logo: '/brokers/unlu-menkul-logo.jpg',
    descKey: 'brokers.unlu',
    bistCommission: '%0,200',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/unlu-trader/id1438592368',
    playStore: 'https://play.google.com/store/apps/details?id=com.unlumenkul.mobile',
    website: 'https://www.unlumenkul.com',
  },
  {
    id: 'tacirler',
    name: 'Tacirler Yatırım',
    logo: '/brokers/tacirler-yatirim-logo.svg',
    logoBg: '#1B2D6E',
    descKey: 'brokers.tacirler',
    bistCommission: '%0,200',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/tacirler-trader/id1438592368',
    playStore: 'https://play.google.com/store/apps/details?id=com.tacirler.mobile',
    website: 'https://tacirler.com.tr',
  },
  {
    id: 'integral',
    name: 'İntegral Yatırım',
    logo: '/brokers/integral-yatirim-logo.webp',
    descKey: 'brokers.integral',
    bistCommission: '%0,200',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/integral-trader/id1438592368',
    playStore: 'https://play.google.com/store/apps/details?id=com.integralyatirim.mobile',
    website: 'https://integralyatirim.com.tr',
  },
  {
    id: 'info',
    name: 'İnfo Yatırım',
    logo: '/brokers/info-yatirim-logo.svg',
    descKey: 'brokers.info',
    bistCommission: '%0,200',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/info-trader/id1438592368',
    playStore: 'https://play.google.com/store/apps/details?id=com.infoyatirim.mobile',
    website: 'https://infoyatirim.com',
  },
  {
    id: 'seker',
    name: 'Şeker Yatırım',
    logo: '/brokers/seker-yatirim-logo.svg',
    descKey: 'brokers.seker',
    bistCommission: '%0,200',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/seker-trader/id1438592368',
    playStore: 'https://play.google.com/store/apps/details?id=com.sekeryatirim.mobile',
    website: 'https://www.sekeryatirim.com.tr',
  },
  {
    id: 'global',
    name: 'Global Menkul',
    logo: '/brokers/global-menkul-logo.png',
    descKey: 'brokers.global',
    bistCommission: '%0,200',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/global-trader/id1438592368',
    playStore: 'https://play.google.com/store/apps/details?id=com.globalmenkul.mobile',
    website: 'https://www.global.com.tr',
  },
  {
    id: 'ahlatci',
    name: 'Ahlatçı Yatırım',
    logo: '/brokers/ahlatci-yatirim-logo.svg',
    descKey: 'brokers.ahlatci',
    bistCommission: '%0,188',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/ahlatci-trader/id1438592368',
    playStore: 'https://play.google.com/store/apps/details?id=com.ahlatciyatirim.mobile',
    website: 'https://www.ahlatciyatirim.com.tr',
  },
  {
    id: 'piramit',
    name: 'Piramit Menkul',
    logo: '/brokers/piramit-menkul-logo.png',
    descKey: 'brokers.piramit',
    bistCommission: '%0,200',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/piramit-trader/id1438592368',
    playStore: 'https://play.google.com/store/apps/details?id=com.piramitmenkul.mobile',
    website: 'https://www.piramitmenkul.com.tr',
  },
  {
    id: 'burgan',
    name: 'Burgan Yatırım',
    logo: '/brokers/burgan-yatirim-logo.svg',
    descKey: 'brokers.burgan',
    bistCommission: '%0,200',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/burgan-trader/id1438592368',
    playStore: 'https://play.google.com/store/apps/details?id=com.burganyatirim.mobile',
    website: 'https://www.burganyatirim.com.tr',
  },
  {
    id: 'hedef',
    name: 'Hedef Yatırım',
    logo: '/brokers/hedef-yatirim-logo.svg',
    descKey: 'brokers.hedef',
    bistCommission: '%0,200',
    usCommission: '%0,299',
    euCommission: '%0,299',
    appStore: 'https://apps.apple.com/tr/app/hedef-trader/id1438592368',
    playStore: 'https://play.google.com/store/apps/details?id=com.hedefyatirim.mobile',
    website: 'https://www.hedefyatirimbankasi.com.tr',
  },
]

export default function BrokersClient() {
  const lang = useLocale()

  return (
    <main className="max-w-7xl mx-auto py-5 px-4 sm:px-6 lg:px-4">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-[13px] mb-6 overflow-hidden whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
        <Link href="/" className="hover:underline transition-colors shrink-0" style={{ color: 'var(--accent-color)' }}>{t('nav.home', lang)}</Link>
        <span className="select-none shrink-0">/</span>
        <span className="font-semibold truncate" style={{ color: 'var(--text-main)' }}>{t('brokers.title', lang)}</span>
      </nav>

      {/* Heading */}
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold tracking-tight m-0" style={{ color: 'var(--text-main)' }}>
          {t('brokers.h1', lang)}
        </h1>
        <p className="text-sm mt-2 m-0" style={{ color: 'var(--text-muted)' }}>
          {t('brokers.desc', lang)}
        </p>
      </div>

      {/* Platform Cards */}
      <div
        className="rounded-xl border overflow-hidden mb-8 p-4 sm:p-6"
        style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
      >
        <h2 className="text-lg font-bold mb-4 m-0" style={{ color: 'var(--text-main)' }}>
          {t('brokers.platforms', lang)}
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {brokers.map(b => {
            const detail = allBrokers.find(d => d.id === b.id)
            return (
            <div
              key={b.id}
              className="flex flex-col items-center gap-2.5 p-4 rounded-xl border cursor-pointer transition-all duration-200 hover:scale-[1.04] hover:shadow-md"
              style={{ borderColor: 'var(--border-color)' }}
              onClick={() => {
                if (detail) {
                  trackAffiliateClick(b.id, b.name, 'brokers_platform', detail.website)
                  window.open(detail.website, '_blank')
                }
              }}
            >
              <div className="w-14 h-14 rounded-xl flex-shrink-0 flex items-center justify-center overflow-hidden p-1" style={{ backgroundColor: detail?.logoBg || '#ffffff' }}>
                <img src={b.logo} alt={b.name} className="w-full h-full object-contain" />
              </div>
              <span className="text-[13px] font-semibold text-center leading-tight" style={{ color: 'var(--text-main)' }}>
                {b.name}
              </span>
              <span
                className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full"
                style={{ backgroundColor: 'var(--secondary-color)', color: '#fff' }}
              >
                {formatCommission(b.commissionPct, lang)}
              </span>
            </div>
          )})}
        </div>
      </div>

      {/* Commission Comparison Table */}
      <div
        className="rounded-xl border overflow-hidden mb-8"
        style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
      >
        <div className="p-4 sm:p-6">
          <h2 className="text-lg font-bold mb-4 m-0" style={{ color: 'var(--text-main)' }}>
            {t('brokers.commission', lang)}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr>
                  <th scope="col" className="text-left py-3 px-4 font-semibold" style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)' }}>{t('th.institution', lang)}</th>
                  <th scope="col" className="text-center py-3 px-4 font-semibold whitespace-nowrap" style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)' }}>{t('cat.bist', lang)}</th>
                  <th scope="col" className="text-center py-3 px-4 font-semibold whitespace-nowrap" style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)' }}>{t('cat.us', lang)}</th>
                  <th scope="col" className="text-center py-3 px-4 font-semibold whitespace-nowrap" style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)' }}>{t('cat.eu', lang)}</th>
                </tr>
              </thead>
              <tbody>
                {allBrokers.map((b, i) => (
                  <tr key={b.id} style={{ backgroundColor: i % 2 === 0 ? 'transparent' : 'var(--bg-body)' }}>
                    <td className="py-3 px-4" style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden p-0.5" style={{ backgroundColor: b.logoBg || '#ffffff' }}>
                          <img src={b.logo} alt={b.name} className="w-full h-full object-contain" />
                        </div>
                        <span className="font-semibold" style={{ color: 'var(--text-main)' }}>{b.name}</span>
                      </div>
                    </td>
                    <td className="text-center py-3 px-4 font-semibold tabular-nums whitespace-nowrap" style={{ color: b.bistCommission.includes('₺0') || b.bistCommission === '₺0' ? 'var(--chart-up)' : 'var(--text-main)', borderBottom: '1px solid var(--border-color)' }}>
                      {b.bistCommission}
                    </td>
                    <td className="text-center py-3 px-4 font-semibold tabular-nums whitespace-nowrap" style={{ color: b.usCommission.includes('$0') ? 'var(--chart-up)' : 'var(--text-main)', borderBottom: '1px solid var(--border-color)' }}>
                      {b.usCommission}
                    </td>
                    <td className="text-center py-3 px-4 font-semibold tabular-nums whitespace-nowrap" style={{ color: b.euCommission.includes('\u20AC0') ? 'var(--chart-up)' : 'var(--text-main)', borderBottom: '1px solid var(--border-color)' }}>
                      {b.euCommission}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detailed Broker Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {allBrokers.map(b => (
          <a
            key={b.id}
            href={b.website}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-xl border overflow-hidden p-5 no-underline transition-all duration-200 hover:scale-[1.02] hover:shadow-lg"
            style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)', display: 'block' }}
            onClick={() => trackAffiliateClick(b.id, b.name, 'brokers_page', b.website)}
          >
            {/* Top: Logo + Name + Badge */}
            <div className="flex items-center gap-4 mb-3">
              <div className="w-16 h-16 rounded-xl flex-shrink-0 flex items-center justify-center overflow-hidden p-1.5" style={{ backgroundColor: b.logoBg || '#ffffff' }}>
                <img src={b.logo} alt={b.name} className="w-full h-full object-contain" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-lg font-bold m-0" style={{ color: 'var(--text-main)' }}>{b.name}</h3>
                  {b.highlightKey && (
                    <span
                      className="text-[11px] font-bold px-2.5 py-0.5 rounded-full shrink-0"
                      style={{ backgroundColor: 'var(--chart-up)', color: '#fff' }}
                    >
                      {t(b.highlightKey, lang)}
                    </span>
                  )}
                </div>
                <p className="text-[13px] mt-1 m-0 leading-snug" style={{ color: 'var(--text-muted)' }}>
                  {t(b.descKey, lang)}
                </p>
              </div>
            </div>

            {/* Commission Grid */}
            <div className="grid grid-cols-3 gap-3 mt-4">
              {[
                { labelKey: 'cat.bist', value: b.bistCommission },
                { labelKey: 'cat.us', value: b.usCommission },
                { labelKey: 'cat.eu', value: b.euCommission },
              ].map(c => (
                <div
                  key={c.labelKey}
                  className="text-center py-2 rounded-lg"
                  style={{ backgroundColor: 'var(--bg-body)' }}
                >
                  <div className="text-[10px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: 'var(--text-muted)' }}>{t(c.labelKey, lang)}</div>
                  <div className="text-sm font-bold tabular-nums" style={{ color: 'var(--text-main)' }}>{c.value}</div>
                </div>
              ))}
            </div>

            {/* App Store / Google Play */}
            <div className="flex items-center gap-3 mt-4">
              <span
                className="flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-lg"
                style={{ backgroundColor: 'var(--bg-body)', color: 'var(--text-muted)' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                App Store
              </span>
              <span
                className="flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-lg"
                style={{ backgroundColor: 'var(--bg-body)', color: 'var(--text-muted)' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M3.18 23.77L14.27 12.68L3.18.23C2.83-.12 2.27-.04 2.02.42L.18 4.11V19.89L2.02 23.58C2.27 24.04 2.83 24.12 3.18 23.77ZM14.27 12.68L17.6 9.35L5.72 2.6L14.27 12.68ZM14.27 12.68L5.72 22.76L17.6 16.01L14.27 12.68ZM21.83 10.95L18.5 9.03L14.84 12.68L18.5 16.34L21.83 14.42C22.94 13.78 22.94 11.59 21.83 10.95Z"/></svg>
                Google Play
              </span>
            </div>
          </a>
        ))}
      </div>

      {/* Disclaimer */}
      <div
        className="rounded-xl border overflow-hidden mt-8 p-4 sm:p-5"
        style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
      >
        <p className="text-[12px] leading-relaxed m-0" style={{ color: 'var(--text-muted)' }}>
          {t('brokers.disclaimer', lang)}
        </p>
      </div>
    </main>
  )
}
